import React, { useMemo } from "react";

import "@local-logic/maps/style.css";

import { useTracking } from "@local-logic/core/tracking";
import { useLocalContent } from "~/sdks/LocalContent/context";
import { useResponsiveBreakpoints } from "~/hooks/useResponsiveBreakpoints";

import {
  LocalLogicMap,
  type Marker,
  type Source,
  type LayerType,
} from "@local-logic/maps";
import {
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "~/sdks/LocalContent/types";
import { icons } from "~/components/PoiIcon";

import {
  DEFAULT_MIN_ZOOM,
  DEFAULT_MAX_ZOOM,
  MAP_PADDING,
  DIRECTIONS_PATH_COLOR,
} from "~/sdks/LocalContent/constants";

import { getIdFromMarker } from "~/sdks/LocalContent/utils/getIdFromMarker";

import type { School } from "@local-logic/client";

import { ClusterPopupContent } from "./ClusterPopupContent";
import { POIPopupContent } from "./POIPopupContent";

import * as styles from "./styles";

export function Map({ children }: { children?: React.ReactNode }) {
  const breakpoints = useResponsiveBreakpoints();
  const tracking = useTracking();
  const {
    activeScore,
    isCommuteCalculatorOpen,
    centerMarker,
    location,
    map,
    activeCategoryData,
    selectedPoiSubcategory,
    isMarkersLoading,
    markersData,
    activeMarker,
    activeMarkerDetails,
    commuteData,
    handleMarkerClick,
  } = useLocalContent();

  const mapProviderConfig = useMemo(() => {
    const mapProvider = {
      name: map.mapProvider?.name || "maptiler",
      apiKey: map.mapProvider?.key || import.meta.env.VITE_MAPTILER_KEY,
      theme: map.mapProvider?.theme,
    };

    if (typeof map.mapProvider !== "undefined") {
      if (
        map.mapProvider.name !== "maptiler" &&
        typeof map.mapProvider.key === "undefined"
      ) {
        throw new Error(
          `Map provider key is required. Please provide a valid key for the ${map.mapProvider.name} map provider.`
        );
      }

      if (
        map.mapProvider.name === "maptiler" &&
        typeof map.mapProvider.theme === "string" &&
        typeof map.mapProvider.key === "undefined"
      ) {
        mapProvider.theme = undefined; // Use default MapTiler theme if no key is provided
        // eslint-disable-next-line no-console
        console.warn(
          "MapTiler theme is set but no API key is provided. Please provide a valid MapTiler API key. Using default LocalLogic MapTiler configuration and theme."
        );
      }
    }

    return mapProvider;
  }, [map.mapProvider]);

  const markers = useMemo(() => {
    if (
      selectedPoiSubcategory === null ||
      isMarkersLoading ||
      typeof markersData === "undefined" ||
      typeof markersData[selectedPoiSubcategory] === "undefined"
    ) {
      return [];
    }

    return markersData[selectedPoiSubcategory].map(
      (marker) =>
        ({
          id: getIdFromMarker(marker.lat, marker.lng, marker.name),
          latitude: marker.lat,
          longitude: marker.lng,
          name: marker.name,
          icon: icons[selectedPoiSubcategory],
        }) as Marker
    );
  }, [selectedPoiSubcategory, isMarkersLoading, markersData]);

  const commuteMarkers = useMemo(() => {
    if (commuteData === null) {
      return [];
    }

    if (
      markers.find(
        (marker) =>
          marker.latitude === commuteData.destination.lat &&
          marker.longitude === commuteData.destination.lng
      )
    ) {
      return [];
    }

    return [
      {
        id: "commute-destination",
        latitude: commuteData.destination.lat,
        longitude: commuteData.destination.lng,
        name: commuteData.label,
        icon: icons.other as Marker["icon"],
        defaultColor: true,
        disableClick: true,
      },
    ];
  }, [markers, commuteData]);

  const combinedMarkers = useMemo(
    () => [...markers, ...commuteMarkers],
    [markers, commuteMarkers]
  );

  const mapPadding = useMemo(() => {
    const padding = breakpoints.sm ? MAP_PADDING.desktop : MAP_PADDING.mobile;

    if (isCommuteCalculatorOpen) {
      return {
        top: padding.top + 200, // Adjust for the commute calculator header height
        bottom: padding.bottom,
        left: padding.left,
        right: padding.right,
      };
    }

    return padding;
  }, [breakpoints.lg, isCommuteCalculatorOpen]);

  const schoolLayerSources = useMemo(() => {
    const activeSchoolMarkerDetails = activeMarkerDetails as School;
    if (
      typeof activeSchoolMarkerDetails?.catchments === "undefined" ||
      activeSchoolMarkerDetails.catchments?.length === 0
    ) {
      return undefined;
    }

    const sources: Source[] = [];

    activeSchoolMarkerDetails.catchments.forEach((catchment, index) => {
      if (
        typeof catchment.geometry !== "undefined" &&
        catchment.geometry.length > 0
      ) {
        sources.push({
          key: `school-catchment-${index}`,
          layer: [
            {
              type: "fill" as LayerType,
              color: activeCategoryData?.colors?.fill || "#008491",
            },
            {
              type: "line" as LayerType,
              color: activeCategoryData?.colors?.fill || "#008491",
            },
          ],
          geometry: catchment.geometry,
        });
      }
    });

    return sources;
  }, [activeMarkerDetails, activeCategoryData]);

  const routeSources = useMemo(() => {
    if (
      !commuteData ||
      typeof commuteData.geometry === "undefined" ||
      commuteData.geometry?.length === 0
    ) {
      return undefined;
    }

    const sources: Source[] = [];

    sources.push({
      key: "commute-route",
      type: "LineString",
      layer: [
        {
          type: "line" as LayerType,
          color: DIRECTIONS_PATH_COLOR,
        },
      ],
      geometry: commuteData.geometry,
    });

    return sources;
  }, [commuteData, activeCategoryData]);

  const handleMapMarkerClick = (
    marker: Parameters<typeof handleMarkerClick>[0]
  ) => {
    handleMarkerClick(marker);

    if (!activeScore || !selectedPoiSubcategory || !marker) {
      return;
    }

    const eventAttributes: Record<string, boolean | string | number> = {
      score: activeScore,
      category: selectedPoiSubcategory,
    };

    if (marker.markers.length > 1) {
      eventAttributes.isCluster = true;
      eventAttributes.clusterSize = marker.markers.length;
      eventAttributes.lat = marker.latitude;
      eventAttributes.lng = marker.longitude;
    } else if (marker.markers.length === 1) {
      Object.entries(marker.markers[0]).forEach(([key, value]) => {
        if (
          typeof value === "boolean" ||
          typeof value === "string" ||
          typeof value === "number"
        ) {
          eventAttributes[key] = value;
        }
      });
    }

    tracking.postEvent({
      eventType: TrackingPipelineEvents.MAP_POI_SELECTED,
      eventAttributes: Object.entries(eventAttributes).map(([key, value]) => ({
        label: key,
        data: value,
      })),
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.MAP_POI_SELECTED,
      eventAttributes,
    });
  };

  // If location is not set or invalid, return a message
  // lat and lng values of 0 are considered invalid for this context
  if (!location || !location.lat || !location.lng) {
    return <div className={styles.container}>Location not set</div>;
  }

  return (
    <div className={styles.container}>
      <LocalLogicMap.Root
        mapProvider={mapProviderConfig}
        center={{ latitude: location.lat, longitude: location.lng }}
        zoom={map.zoom}
        minZoom={DEFAULT_MIN_ZOOM}
        maxZoom={DEFAULT_MAX_ZOOM}
        zoomPosition="bottom-right"
        pitch={map.pitch}
        bearing={map.bearing}
        cooperativeGestures={map.cooperativeGestures}
        activeMarker={activeMarker}
      >
        <LocalLogicMap.BaseMap padding={mapPadding}>
          <LocalLogicMap.Layers sources={schoolLayerSources} />
          <LocalLogicMap.Layers sources={routeSources} />

          <LocalLogicMap.Markers
            markers={combinedMarkers}
            colors={activeCategoryData?.colors}
            clusterIcon={
              (selectedPoiSubcategory
                ? icons[selectedPoiSubcategory]
                : undefined) as Marker["icon"]
            }
            onClick={handleMapMarkerClick}
          />

          <LocalLogicMap.Popup
            latitude={activeMarker?.latitude}
            longitude={activeMarker?.longitude}
            onClose={() => handleMarkerClick(undefined)}
          >
            <ClusterPopupContent />
            <POIPopupContent />
          </LocalLogicMap.Popup>

          {centerMarker && (
            <LocalLogicMap.CenterPin
              marker={{
                id: "local-content-center-pin",
                latitude: centerMarker.lat,
                longitude: centerMarker.lng,
                name: centerMarker.addressLabel || "Center Pin",
              }}
            />
          )}
        </LocalLogicMap.BaseMap>
      </LocalLogicMap.Root>

      {children}
    </div>
  );
}
